import React, { createContext, useContext, useId, useRef, useState } from 'react'
import { FleetGrid, useGridList } from '../../../../shared/sso-workspace/fleet-grid'
import { Notice } from '../../../../shared/sso-workspace/fleet-page'
import { TableAgClient } from '../../../../shared/table-builder/table-ag-client'
import { ColumnFormat, ColumnText } from '../../../../shared/table-builder/table-columns'
import { serverMessage } from '../../../../shared/error/server-message'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { dateTimeText, utcText } from '../../../../shared/date/local-time'
import { getRecords } from './consent-log-api'
import { recordRow } from './consent-log-words'
import { SnapshotDrawer } from './snapshot-drawer'

// Module-level cells, so AG Grid keeps them.
const whenCell = cell => (cell.data && cell.data.chosenAt ? <span title={utcText(cell.data.chosenAt)}>{dateTimeText(cell.data.chosenAt)}</span> : '')
const monoCell = field => cell => (cell.data ? <span className="sw-mono">{cell.data[field]}</span> : '')

// The cells are module-level (AG Grid keeps them), so View reaches the tab's
// drawer through context. A click on a control inside a row is the control's
// own: it never raises the grid's onRowClicked.
const OpenRecord = createContext(() => {})
const ViewButton = ({ row }) => {
  const open = useContext(OpenRecord)
  return <button type="button" className="btn btn-sm sw-btn" aria-label={`View record ${row.seq}`} onClick={() => open(row)}>View</button>
}
const viewCell = cell => (cell.data ? <ViewButton row={cell.data} /> : '')

// A consent ID's history, oldest first. A record's View opens its version's
// cookie list and wording in a drawer.
export const LookupTab = () => {
  const inputId = useId()
  const [consentId, setConsentId] = useState('')
  const [rows, setRows] = useState(undefined)
  const [asked, setAsked] = useState('')
  const [open, setOpen] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, failure, setError] = useErrorLine()
  const tableRef = useRef()
  const gridList = useGridList(rows)

  const search = async e => {
    e.preventDefault()
    const id = consentId.trim()
    if (!id || busy) return
    setBusy(true)
    setError(null)
    setOpen(null)
    try {
      const body = await getRecords(id)
      setRows(Array.isArray(body) ? body.filter(r => r && typeof r === 'object').map(recordRow) : [])
      setAsked(id)
    } catch (err) {
      setRows(undefined)
      setAsked('')
      setError(serverMessage(err, "The consent log couldn't be read."), err)
    }
    setBusy(false)
  }

  return (
    <>
      <form className="ak-filters" onSubmit={search}>
        <div className="ak-filter">
          <label className="sw-label" htmlFor={inputId}>Consent ID</label>
          <input id={inputId} type="text" className="form-control sw-mono" autoComplete="off" spellCheck={false} value={consentId} onChange={e => setConsentId(e.target.value)} />
        </div>
        <div className="ak-filter">
          <button type="submit" className="btn sw-btn-primary" disabled={!consentId.trim() || busy}>Look up</button>
        </div>
      </form>

      {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}
      {rows && rows.length === 0 && <p className="sw-empty-state">{`No records for ${asked}.`}</p>}
      {rows && rows.length > 0 &&
        <FleetGrid title="Records" subtitle={`Every choice recorded for ${asked}, oldest first.`} tableRef={tableRef} exportName="consent-records">
          <OpenRecord.Provider value={setOpen}>
          <TableAgClient ref={tableRef} apiList={gridList} sortCol="seq" onRowClicked={row => row && setOpen(row)}>
            <ColumnText field="seq" header="#" sortable={false} fit="content" />
            <ColumnFormat field="chosenAt" header="Chosen" sortable={false} fit="content" render={whenCell} />
            <ColumnText field="actionText" header="Action" sortable={false} fit="content" />
            <ColumnText field="choicesText" header="Choices" sortable={false} flex={1.4} />
            <ColumnText field="bannerText" header="Banner" sortable={false} fit="content" />
            <ColumnText field="modeReasonText" header="Rules" sortable={false} fit="content" />
            <ColumnText field="location" header="Location" sortable={false} fit="content" />
            <ColumnFormat field="ipMasked" header="IP address" sortable={false} fit="content" render={monoCell('ipMasked')} />
            <ColumnText field="userAgent" header="Browser" sortable={false} flex={1.4} />
            <ColumnFormat field="pagePath" header="Page" sortable={false} flex={1} render={monoCell('pagePath')} />
            <ColumnText field="siteText" header="Site" sortable={false} fit="content" />
            <ColumnFormat field="version" header="Version" sortable={false} fit="content" render={monoCell('version')} />
            <ColumnFormat field="id" header="Details" sortable={false} fit="content" render={viewCell} />
          </TableAgClient>
          </OpenRecord.Provider>
        </FleetGrid>}
      {open && <SnapshotDrawer row={open} onClose={() => setOpen(null)} />}
    </>
  )
}
